import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";

const inputCls = "w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm focus:outline-none focus:border-ember transition-colors";

const METER_LABELS = {
  email_parses: "AI email parses",
  assistant_messages: "Assistant messages",
  live_searches: "Live searches",
};

export default function AccountSection() {
  const { logout } = useAuth();
  const [usage, setUsage] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/plan/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, []);

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/me", { data: { confirm, password } });
      toast.success("Your account has been deleted");
      await logout();
      window.location.href = "/auth";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div id="account" className="space-y-6" data-testid="account-section">
      {usage && usage.plan === "free" && (
        <div className="bg-surface rounded-card border border-hairline p-6" data-testid="plan-usage-card">
          <div className="flex items-center justify-between mb-5">
            <p className="label-tag">Free plan · monthly usage</p>
            <span className="font-mono text-[10px] text-sand-400 tracking-wider">{usage.month}</span>
          </div>
          <div className="space-y-4">
            {Object.entries(usage.meters).map(([k, m]) => (
              <div key={k} data-testid={`plan-meter-${k}`}>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-sand-600">{METER_LABELS[k] || k}</span>
                  <span className={`font-mono ${m.remaining === 0 ? "text-ember font-semibold" : "text-sand-500"}`}>
                    {m.used} / {m.limit}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-sand-100 overflow-hidden">
                  <div className={`h-full rounded-full ${m.remaining === 0 ? "bg-ember" : "bg-dusk"}`}
                    style={{ width: `${Math.min(100, (m.used / m.limit) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-sand-400 mt-4">Meters reset on the 1st of each month. Wandr Pro is coming soon.</p>
        </div>
      )}

      <div className="bg-surface rounded-card border border-red-200 p-6" data-testid="danger-zone">
        <p className="label-tag text-red-600 mb-2 flex items-center gap-2">
          <AlertTriangle size={12} /> Danger zone
        </p>
        <p className="text-sm text-sand-600 max-w-xl">
          Permanently delete your Wandr account and everything in it — trips, reservations,
          travel profile, chats, budget, presence history. This cannot be undone.
        </p>
        <button
          onClick={() => { setConfirm(""); setPassword(""); setOpen(true); }}
          className="btn-swiss text-sm mt-4 border border-red-300 text-red-600 hover:bg-red-50"
          data-testid="delete-account-btn"
        >
          Delete my account
        </button>
        <p className="text-xs text-sand-400 mt-5">
          <Link to="/privacy" className="hover:text-ember underline underline-offset-2" data-testid="link-privacy">Privacy Policy</Link>
          {" · "}
          <Link to="/terms" className="hover:text-ember underline underline-offset-2" data-testid="link-terms">Terms of Service</Link>
        </p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>
              This permanently erases your account and all data. There is no recovery.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="label-tag block mb-2">Type DELETE to confirm</label>
              <input className={`${inputCls} font-mono`} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="DELETE" data-testid="delete-confirm-input" />
            </div>
            <div>
              <label className="label-tag block mb-2">Your password</label>
              <input type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="delete-password-input" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-swiss btn-ghost text-sm" data-testid="delete-cancel-btn">
              Cancel
            </button>
            <button
              onClick={doDelete}
              disabled={busy || confirm.trim().toUpperCase() !== "DELETE" || !password}
              className="btn-swiss text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
              data-testid="delete-account-confirm-btn"
            >
              {busy ? "Deleting…" : "Delete forever"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
